import { m } from 'framer-motion';
import { useState, useCallback } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem, { OnboardingItem, WorkspaceAccessItem, BrandInfringementItem } from './notification-item';
import { Invitation, useInvitationsQuery, WorkspaceList } from 'src/context/api/workspaces';
import { Avatar, Button, LinearProgress, ListItemAvatar, ListItemButton, ListItemText, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { Notification, useExportJobsQuery } from 'src/context/api/data/api';
import useNotifications from './use-notifications';
import { filterByType } from './notifications.utils';
import { computeTabCounts } from './notifications-popover.helpers';
import { splitExportJobs, exportDownloadUrl } from './exports.helpers';
import { useCurrentUserQuery } from 'src/context/api/auth/api';

// ----------------------------------------------------------------------


// ----------------------------------------------------------------------

/**
 * Notifications Popover
 *
 * This component is used to display the notifications popover.
 * It is used to display the notifications, export jobs, and other information.
 *
 * @returns {JSX.Element} The Notifications Popover component
 */
export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const [currentTab, setCurrentTab] = useState('all');
  const workspace: any = useCurrentWorkspace();
  const theme: any = useTheme();
  const [exportSelected, setExportSelected] = useState<"new" | "previous">("new");
  const user = useCurrentUserQuery();


  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const invitations = useInvitationsQuery(null);

  const { data: exportJobs } = useExportJobsQuery(undefined, { pollingInterval: 1 * 90 * 1000 });

  const { visibleNotifications } = useNotifications();

  const role = user.data?.role?.toLowerCase();
  const isCustomerOrConsultant = role === 'customer' || role === 'consultant';

  // Onboarding notifications must never surface to customer/consultant roles:
  // not in the badge count, not in any rendered list (only the onboard TAB
  // was previously hidden for them). Derive a single role-filtered list and
  // use it everywhere (badge, tab counts, all rendered lists) so they stay
  // in sync — single source of truth for onboarding visibility.
  const roleVisibleNotifications = isCustomerOrConsultant
    ? visibleNotifications.filter((n) => n.type !== 'onboarding')
    : visibleNotifications;

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const { active: activeExportJobs } = splitExportJobs(exportJobs?.jobs);
  // Counts must match the cards each list renders, including the non-
  // notification ones: invitations in the all tab, live jobs in the export tab.
  const tabCounts = computeTabCounts(roleVisibleNotifications, {
    invitations: invitations?.data?.length ?? 0,
    activeExportJobs: activeExportJobs.length,
  });

  const rawTabs = [
    {
      value: 'all',
      label: t('dashboard.global.all'),
      count: tabCounts.all,
    },
    {
      value: 'brand_infringement',
      label: 'Brand Infringement',
      count: tabCounts.brandInfringement,
    },
    {
      value: 'export',
      label: t('dashboard.global.export'),
      count: tabCounts.export,
    },
    {
      value: 'onboard',
      label: 'Onboarding',
      count: tabCounts.onboard,
    },
  ];

  const TABS = isCustomerOrConsultant ? rawTabs.filter((tab) => tab.value !== 'onboard') : rawTabs;

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const { data: brands } = useGetBrandsQuery({ bqTable: workspace?.bqTable }, { skip: !workspace?.bqTable });

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Box sx={{
          p: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#28323D' : ' #F4F6F8',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          borderRadius: '10px',
          padding: '10px',
          width: 44
        }}>
          <Box component="img" src={`https://www.google.com/s2/favicons?domain=${workspace?.url}&sz=64`} sx={{ width: 24, height: 24, borderRadius: '5px' }} />
        </Box>
      </Stack>
    </ListItemAvatar>
  );

  // Generic card for notification types that don't have a dedicated component
  // (brand_infringement, export-type notifications). Reuses the same visual
  // language (avatar + unread dot + title/content + dismiss action).
  const renderGenericNotificationCard = (notification: Notification) => (
    <ListItemButton
      key={notification.id}
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {notification.status === 'unread' && (
        <Box
          sx={{
            top: 26,
            width: 8,
            height: 8,
            right: 20,
            borderRadius: '50%',
            bgcolor: 'info.main',
            position: 'absolute',
          }}
        />
      )}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        <ListItemText
          disableTypography
          primary={notification.title}
          secondary={
            <Stack direction="row" alignItems="center" sx={{ typography: 'caption', color: 'text.disabled' }}>
              {notification.content}
            </Stack>
          }
        />
        <Stack direction="row" sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={(notification as any).dismiss}>
            OK
          </Button>
        </Stack>
      </Stack>
    </ListItemButton>
  );

  const renderNotificationCard = (notification: Notification) => {
    switch (notification.type) {
      case 'workspace_access':
        return <WorkspaceAccessItem key={notification.id} notification={notification as any} />;
      case 'onboarding':
        return (
          <OnboardingItem
            key={notification.id}
            title={notification.title}
            content={notification.content}
            workspace={notification.workspace as WorkspaceList}
          />
        );
      case 'brand_infringement':
        return <BrandInfringementItem key={notification.id} notification={notification as any} />;
      default:
        return renderGenericNotificationCard(notification);
    }
  };

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {invitations?.data?.map((invitation: Invitation) => (
          <NotificationItem key={invitation.id} notification={invitation} />
        ))}
        {roleVisibleNotifications
          .filter((notification) => notification.status !== 'dismissed')
          .map((notification) => renderNotificationCard(notification))}
      </List>
    </Scrollbar>
  );

  const handleDrawerNotification = () => {
    drawer.onTrue();
  }

  const renderBrandInfringementList = (
    <Scrollbar>
      <List disablePadding>
        {filterByType(roleVisibleNotifications, 'brand_infringement')
          .filter((notification) => notification.status !== 'dismissed')
          .map((notification) => (
            <BrandInfringementItem key={notification.id} notification={notification as any} />
          ))}
      </List>
    </Scrollbar>
  );

  const renderOnboardList = user.data?.role.toLowerCase() !== "customer" && (
    <>
      <Stack direction="row" justifyContent="space-evenly" alignItems="center" spacing={2} sx={{ mt: 1 }}>
        <Typography sx={{ cursor: 'pointer', borderBottom: exportSelected === "new" ? `2px solid ${theme.palette.primary.main}` : 'none', fontWeight: exportSelected === "new" ? 600 : 400 }} variant="body2" color={exportSelected === "new" ? "primary" : "text.secondary"} onClick={() => setExportSelected("new")}>unread</Typography>
        <Typography sx={{ cursor: 'pointer', borderBottom: exportSelected === "previous" ? `2px solid ${theme.palette.primary.main}` : 'none', fontWeight: exportSelected === "previous" ? 600 : 400 }} variant="body2" color={exportSelected === "previous" ? "primary" : "text.secondary"} onClick={() => setExportSelected("previous")}>read</Typography>
      </Stack>
      <Scrollbar>
        <List disablePadding>
          {filterByType(roleVisibleNotifications, 'onboarding').filter((notification) => (exportSelected === "new" ? notification.status === "unread" : notification.status !== "unread")).map((notification) => (
            <OnboardingItem key={notification.id} title={notification.title} content={notification.content} workspace={notification.workspace as WorkspaceList} />
          ))}
        </List>
      </Scrollbar>
    </>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={handleDrawerNotification}
      >
        <Badge badgeContent={tabCounts.all} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>

        <Divider />

        {currentTab === 'brand_infringement' && renderBrandInfringementList}

        {currentTab === 'export' && (
          <Scrollbar>
            <List disablePadding>
              {/* Live progress: jobs still running/pending/created. Never includes failed jobs
                  (those surface only as an ephemeral toast, see use-notifications.tsx). */}
              {activeExportJobs.map((job) => (
                <ListItemButton
                  key={job.id}
                  disableRipple
                  sx={{
                    p: 2.5,
                    alignItems: 'flex-start',
                    backgroundColor: 'rgba(255, 171, 0, 0.08)',
                    borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar>
                      <Iconify icon="iwwa:file-csv" width={24} />
                    </Avatar>
                  </Stack>

                  <Stack sx={{
                    flexGrow: 1,
                    color: theme.palette.mode === "dark" ? '#fbbf24' : '#d97706',
                    fontWeight: 800
                  }} pl={2}>
                    {job.name}

                    <ListItemText
                      disableTypography
                      secondary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            typography: 'caption',
                            color: theme.palette.mode === "dark" ? '#fbbf24' : '#d97706'
                          }}
                        >
                          {job.status === 'running' && 'Export in progress. Please wait...'}
                          {job.status === 'pending' && 'Export queued for processing.'}
                          {job.status === 'created' && 'Export created. Please wait...'}
                        </Stack>
                      }
                    />

                    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                      <LinearProgress sx={{ width: 100 }} value={50} />
                    </Stack>
                  </Stack>
                </ListItemButton>
              ))}

              {/* Completed exports: rendered from `type=export` notifications (unified
                  notification center), Download button uses the notification's
                  structured job id (see exports.helpers.ts). */}
              {filterByType(roleVisibleNotifications, 'export')
                .filter((notification) => notification.status !== 'dismissed')
                .map((notification) => {
                  const jobId = notification.job_id ?? null;
                  return (
                    <ListItemButton
                      key={notification.id}
                      disableRipple
                      sx={{
                        p: 2.5,
                        alignItems: 'flex-start',
                        backgroundColor: 'rgba(34, 197, 94, 0.08)',
                        borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                      }}
                    >
                      {notification.status === 'unread' && (
                        <Box
                          sx={{
                            top: 26,
                            width: 8,
                            height: 8,
                            right: 20,
                            borderRadius: '50%',
                            bgcolor: 'info.main',
                            position: 'absolute',
                          }}
                        />
                      )}

                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar>
                          <Iconify icon="iwwa:file-csv" width={24} />
                        </Avatar>
                      </Stack>

                      <Stack sx={{
                        flexGrow: 1,
                        color: theme.palette.mode === "dark" ? '#4ade80' : '#16a34a',
                        fontWeight: 800
                      }} pl={2}>
                        {notification.title}

                        <ListItemText
                          disableTypography
                          secondary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              sx={{
                                typography: 'caption',
                                color: theme.palette.mode === "dark" ? '#4ade80' : '#16a34a'
                              }}
                            >
                              {notification.content}
                            </Stack>
                          }
                        />

                        <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                          {jobId && (
                            <LoadingButton
                              onClick={() => {
                                if (jobId) window.open(exportDownloadUrl(jobId), '_blank');
                                (notification as any).read?.();
                              }}
                              size="small"
                              variant="contained"
                              sx={{ backgroundColor: '#22c55e', color: 'white' }}
                            >
                              Download
                            </LoadingButton>
                          )}
                          <Button size="small" variant="outlined" onClick={(notification as any).dismiss}>
                            OK
                          </Button>
                        </Stack>
                      </Stack>
                    </ListItemButton>
                  );
                })}
            </List>
          </Scrollbar>
        )}

        {currentTab === 'all' && renderList}
        {currentTab === 'onboard' && renderOnboardList}
      </Drawer>
    </>
  );
}
